import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';

export interface ChatSession {
  id: string;
  user: number;
  title: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  last_message?: Message;
  message_count: number;
  unread_count: number;
}

export interface Message {
  id: string;
  chat_session: string;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'audio' | 'video';
  file_attachment?: string;
  timestamp: string;
  is_from_user: boolean;
  is_edited: boolean;
  edited_at?: string;
  status?: {
    status: 'sent' | 'delivered' | 'read' | 'processing';
    timestamp: string;
  };
  sender_name: string;
}

export interface UserChatSettings {
  user: number;
  is_online: boolean;
  last_seen: string;
  notification_enabled: boolean;
  typing_indicator: boolean;
}

export interface ChatStats {
  total_sessions: number;
  active_sessions: number;
  total_messages: number;
  user_messages: number;
  ai_messages: number;
}

class ChatBotApiService {
  private apiUrl = `${API_BASE_URL}/chatbot`;

  // Chat Sessions
  async getChatSessions(domainId?: number): Promise<ChatSession[]> {
    try {
      const params = domainId ? { domain: domainId } : {};
      const response = await axios.get(`${this.apiUrl}/sessions/`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      throw error;
    }
  }

  async createChatSession(domainId?: number): Promise<ChatSession> {
    try {
      const data = domainId ? { domain: domainId } : {};
      const response = await axios.post(`${this.apiUrl}/sessions/`, data);
      return response;
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  }

  async getChatSession(sessionId: string): Promise<ChatSession & { messages: Message[] }> {
    try {
      const response = await axios.get(`${this.apiUrl}/sessions/${sessionId}/`);
      return response;
    } catch (error) {
      console.error('Error fetching chat session:', error);
      throw error;
    }
  }

  async updateChatSession(sessionId: string, data: Partial<ChatSession>): Promise<ChatSession> {
    try {
      const response = await axios.put(`${this.apiUrl}/sessions/${sessionId}/`, data);
      return response;
    } catch (error) {
      console.error('Error updating chat session:', error);
      throw error;
    }
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    try {
      await axios.delete(`${this.apiUrl}/sessions/${sessionId}/`);
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  }

  // Messages
  async sendMessage(sessionId: string, content: string, messageType: string = 'text'): Promise<Message> {
    try {
      const response = await axios.post(`${this.apiUrl}/sessions/${sessionId}/send/`, {
        content,
        message_type: messageType
      });
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async getMessages(sessionId: string, pageSize: number = 50, beforeTimestamp?: string): Promise<{ messages: Message[]; has_more: boolean }> {
    try {
      const params: any = { page_size: pageSize };
      if (beforeTimestamp) {
        params.before = beforeTimestamp;
      }
      const response = await axios.get(`${this.apiUrl}/sessions/${sessionId}/messages/`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  async editMessage(messageId: string, content: string): Promise<Message> {
    try {
      const response = await axios.put(`${this.apiUrl}/messages/${messageId}/edit/`, { content });
      return response;
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  async deleteMessage(messageId: string): Promise<void> {
    try {
      await axios.delete(`${this.apiUrl}/messages/${messageId}/delete/`);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  // User Settings
  async getUserChatSettings(): Promise<UserChatSettings> {
    try {
      const response = await axios.get(`${this.apiUrl}/settings/`);
      return response;
    } catch (error) {
      console.error('Error fetching user chat settings:', error);
      throw error;
    }
  }

  async updateUserChatSettings(settings: Partial<UserChatSettings>): Promise<UserChatSettings> {
    try {
      const response = await axios.put(`${this.apiUrl}/settings/`, settings);
      return response;
    } catch (error) {
      console.error('Error updating user chat settings:', error);
      throw error;
    }
  }

  async setTypingIndicator(sessionId: string, isTyping: boolean): Promise<void> {
    try {
      await axios.post(`${this.apiUrl}/sessions/${sessionId}/typing/`, { is_typing: isTyping });
    } catch (error) {
      console.error('Error setting typing indicator:', error);
      throw error;
    }
  }

  // Statistics
  async getChatStats(): Promise<ChatStats> {
    try {
      const response = await axios.get(`${this.apiUrl}/stats/`);
      return response;
    } catch (error) {
      console.error('Error fetching chat stats:', error);
      throw error;
    }
  }
}

export const chatBotApi = new ChatBotApiService();
